import React from 'react';
import {Grid, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {Trans} from '@lingui/react/macro';
import {useGetProviderSplitQuery} from '../../../apis/Provider.api.ts';
import {SplitDimension, splitDimensions} from '../../../types/models/Split';
import ParetoChart from '../../generic/report/ParetoChart.tsx';
import GradientCircularProgress from '../../../../share/components/progress/GradientCircularProgress.tsx';
import {fixedFormat} from '../../../../utils/numberFormat.ts';

type ProviderSplitTabProps = {
    providerId?: string,
};

/**
 * The timber of a provider, cut up by the party that is behind it: the buyer
 * that took it, the contractor that harvested it, or the person that shipped
 * it.
 */
const ProviderSplitTab = ({providerId}: ProviderSplitTabProps) => {

    const [dimension, setDimension] = React.useState<SplitDimension>('buyer');

    const {data, isFetching, isError} = useGetProviderSplitQuery(
        {providerId: providerId!, request: {dimension}},
        {skip: !providerId}
    );

    return (
        <Stack spacing={3} data-testid="Provider-split">
            <ToggleButtonGroup
                exclusive
                size="small"
                value={dimension}
                onChange={(_event, value) => value && setDimension(value)}
                data-testid="Provider-split-dimensions"
            >
                {splitDimensions.map(item => (
                    <ToggleButton key={item.id} value={item.id} data-testid={`Provider-split-${item.id}`}>
                        {item.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {isFetching && !data && (
                <Stack sx={{alignItems: 'center', padding: '2em'}} data-testid="Provider-split-loading">
                    <GradientCircularProgress/>
                </Stack>
            )}

            {isError && (
                <Typography variant="body2" data-testid="Provider-split-error">
                    <Trans>The split could not be loaded.</Trans>
                </Typography>
            )}

            {data && (
                <Grid container spacing={2}>
                    <Grid size={{xs: 12, md: 8}}>
                        <Paper sx={{padding: '1em'}}>
                            <ParetoChart rows={data.rows} measure={data.measure}/>
                        </Paper>
                    </Grid>
                    <Grid size={{xs: 12, md: 4}}>
                        <Paper sx={{padding: '1em'}}>
                            <Table size="small" data-testid="Provider-split-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Trans>Name</Trans></TableCell>
                                        <TableCell align="right"><Trans>Volume (m³)</Trans></TableCell>
                                        <TableCell align="right"><Trans>Share</Trans></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.rows.map(row => (
                                        <TableRow key={row.key} data-testid={`Provider-split-row-${row.key}`}>
                                            <TableCell>{row.label}</TableCell>
                                            <TableCell align="right">{fixedFormat(row.volume)}</TableCell>
                                            <TableCell align="right">{`${row.share.toFixed(1)} %`}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Stack>
    );
};

export default ProviderSplitTab;
